import { CableTermType, EngineeringStatus, MetadataTableName } from '@bechtel/sr-types';
import { get, map } from 'lodash';

export const prepareEnableValues = [null, EngineeringStatus.RETURNED_BY_CHECKER, EngineeringStatus.UNPREPARED];
export const checkEnableValues = [
  EngineeringStatus.RETURNED_BY_APPROVER,
  EngineeringStatus.PREPARED,
  EngineeringStatus.CHECKED,
  EngineeringStatus.RETURNED_BY_CHECKER,
];
export const apprEnableValues = [
  EngineeringStatus.CHECKED,
  EngineeringStatus.APPROVED,
  EngineeringStatus.RETURNED_BY_APPROVER,
];

export const commoditiesWithNotes = [
  MetadataTableName.EQUIPMENT,
  MetadataTableName.EQUIPMENT_CODE,
  MetadataTableName.CABLE,
  MetadataTableName.CABLE_CODE,
  MetadataTableName.RACEWAY,
  MetadataTableName.RACEWAY_CODE,
  MetadataTableName.JUMPER,
  MetadataTableName.VIA,
  MetadataTableName.CABLE_FROM_END,
  MetadataTableName.CABLE_TO_END,
  // TODO: Enable when terms functionality is ready
  // MetadataTableName.CABLE_TERM
];

export enum Operation {
  PRERARE = 'Prepare',
  UNPREPARE = 'Unprepare',
  UNDO_RETURN_BY_CHECKER = 'Undo Return by Checker',
  RETURN_BY_CHECKER = 'Return by Checker',
  UNDO_CHECK = 'Undo Check',
  CHECK = 'Check',
  UNDO_RETURN_BY_APPROVER = 'Undo Return by Approver',
  RETURN_BY_APPROVER = 'Return by Approver',
  UNDO_APPROVE = 'Undo Approve',
  APPROVE = 'Approve',
  ISSUE = 'Issue',
}

export const getIssueInput = (name: string, ids: string[], engStatus: EngineeringStatus, note: string = '') => {
  const issueInput = map(ids, (id) => ({
    id,
    engineeringStatus: engStatus,
    ...(note && note !== '' && { note }), // Include 'note' property conditionally
  }));

  const settingsIssueInput = {
    engineeringStatus: engStatus,
  };

  if (name === MetadataTableName.EQUIPMENT) {
    return { issueEquipmentsInput: issueInput };
  } else if (name === MetadataTableName.EQUIPMENT_CODE) {
    return { issueEquipmentCodesInput: issueInput };
  } else if (name === MetadataTableName.CABLE_CODE) {
    return { issueCableCodesInput: issueInput };
  } else if (name === MetadataTableName.RACEWAY_CODE) {
    return { issueRacewayCodesInput: issueInput };
  } else if (name === MetadataTableName.PROJECT) {
    return { issueProjectsInput: issueInput };
  } else if (name === MetadataTableName.CABLE) {
    return { issueCablesInput: issueInput };
  } else if (name === MetadataTableName.RACEWAY) {
    return { issueRacewaysInput: issueInput };
  } else if (name === MetadataTableName.FIRE_AREA) {
    return { issueFireAreasInput: issueInput };
  } else if (name === MetadataTableName.JUMPER) {
    return { issueJumpersInput: issueInput };
  } else if (name === MetadataTableName.SERVICE_LEVEL_COMPATIBILITY) {
    return { issueServiceLevelCompatibilityInput: settingsIssueInput };
  } else if (name === MetadataTableName.SEPARATION_GROUP_COMPATIBILITY) {
    return { issueSeparationGroupCompatibilityInput: settingsIssueInput };
  } else if (name === MetadataTableName.SERVICE_LEVEL_RACEWAY) {
    return { issueServiceLevelRacewayInput: settingsIssueInput };
  } else if (name === MetadataTableName.VIA) {
    return { issueRoutesInput: issueInput };
  } else if (name === MetadataTableName.CABLE_FROM_END) {
    return { issueCableTermsInput: { issueCableTermsInput: issueInput, term: CableTermType.F } };
  } else if (name === MetadataTableName.CABLE_TO_END) {
    return { issueCableTermsInput: { issueCableTermsInput: issueInput, term: CableTermType.T } };
  }
  return null;
};

export const getIssueErrors = (name: string, data: any) => {
  if (name === MetadataTableName.EQUIPMENT) {
    return get(data, 'issueEquipments.errors') ?? [];
  } else if (name === MetadataTableName.EQUIPMENT_CODE) {
    return get(data, 'issueEquipmentCodes.errors') ?? [];
  } else if (name === MetadataTableName.CABLE_CODE) {
    return get(data, 'issueCableCodes.errors') ?? [];
  } else if (name === MetadataTableName.RACEWAY_CODE) {
    return get(data, 'issueRacewayCodes.errors') ?? [];
  } else if (name === MetadataTableName.PROJECT) {
    return get(data, 'issueProjects.errors') ?? [];
  } else if (name === MetadataTableName.CABLE) {
    return get(data, 'issueCables.errors') ?? [];
  } else if (name === MetadataTableName.RACEWAY) {
    return get(data, 'issueRaceways.errors') ?? [];
  } else if (name === MetadataTableName.FIRE_AREA) {
    return get(data, 'issueFireAreas.errors') ?? [];
  } else if (name === MetadataTableName.JUMPER) {
    return get(data, 'issueJumpers.errors') ?? [];
  } else if (name === MetadataTableName.SERVICE_LEVEL_COMPATIBILITY) {
    return get(data, 'issueServiceLevelCompatibilities.errors') ?? [];
  } else if (name === MetadataTableName.SEPARATION_GROUP_COMPATIBILITY) {
    return get(data, 'issueSeparationGroupCompatibilities.errors') ?? [];
  } else if (name === MetadataTableName.SERVICE_LEVEL_RACEWAY) {
    return get(data, 'issueServiceLevelRaceways.errors') ?? [];
  } else if (name === MetadataTableName.VIA) {
    return get(data, 'issueRoutes.errors') ?? [];
  } else if (name === MetadataTableName.CABLE_FROM_END || name === MetadataTableName.CABLE_TO_END) {
    return get(data, 'issueCableTerms.errors') ?? [];
  }
  return null;
};

export const getIssueData = (name: string, data: any) => {
  if (name === MetadataTableName.EQUIPMENT) {
    return get(data, 'issueEquipments.equipments') ?? [];
  } else if (name === MetadataTableName.EQUIPMENT_CODE) {
    return get(data, 'issueEquipmentCodes.equipmentCodes') ?? [];
  } else if (name === MetadataTableName.CABLE_CODE) {
    return get(data, 'issueCableCodes.cableCodes') ?? [];
  } else if (name === MetadataTableName.RACEWAY_CODE) {
    return get(data, 'issueRacewayCodes.racewayCodes') ?? [];
  } else if (name === MetadataTableName.PROJECT) {
    return get(data, 'issueProjects.projects') ?? [];
  } else if (name === MetadataTableName.RACEWAY) {
    return get(data, 'issueRaceways.raceways') ?? [];
  } else if (name === MetadataTableName.FIRE_AREA) {
    return get(data, 'issueFireAreas.fireAreas') ?? [];
  } else if (name === MetadataTableName.JUMPER) {
    return get(data, 'issueJumpers.jumpers') ?? [];
  } else if (name === MetadataTableName.SERVICE_LEVEL_COMPATIBILITY) {
    return get(data, 'issueServiceLevelCompatibilities.serviceLevelCompatibilities') ?? [];
  } else if (name === MetadataTableName.SEPARATION_GROUP_COMPATIBILITY) {
    return get(data, 'issueSeparationGroupCompatibilities.separationGroupCompatibilities') ?? [];
  } else if (name === MetadataTableName.SERVICE_LEVEL_RACEWAY) {
    return get(data, 'issueServiceLevelRaceways.serviceLevelRaceways') ?? [];
  } else if (name === MetadataTableName.VIA) {
    return get(data, 'issueRoutes.cables') ?? [];
  } else if (name === MetadataTableName.CABLE_FROM_END || name === MetadataTableName.CABLE_TO_END) {
    return get(data, 'issueCableTerms.cables') ?? [];
  } else if (name === MetadataTableName.CABLE) {
    return get(data, 'issueCables.cables') ?? [];
  }

  return null;
};
